import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { generateSlug } from '../../utils/slugUtil';
import { UserAuth } from '../../AuthContext';
import { Box, Flex, Text, Heading } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };
const inp: React.CSSProperties = { display:'block',width:'100%',padding:'12px 14px',background:'#0D1118',border:`1px solid #1E2330`,borderRadius:8,color:'#F0F4FF',fontSize:14,fontFamily:"'Rajdhani','Arial',sans-serif",boxSizing:'border-box' as any };
const lbl: React.CSSProperties = { display:'block',fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:'#19E6CF',letterSpacing:'0.15em',marginBottom:6,textTransform:'uppercase' as const };

const GAME_TYPES = ["Texas Hold'em",'Omaha','Mixed Games',"Dealer's Choice",'Other'];
const BUY_IN_RANGES = ['Free','Under $20','$20-$50','$50-$100','$100-$200','$200-$500','$500+','Varies'];

const CreateVenuePage: FC = () => {
  const { user } = UserAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name:'', description:'', schedule:'', game_type:'', buy_in_range:'',
    area:'', city:'', province:'',
    brand_color_primary:'#19E6CF', brand_color_secondary:'#D4A017',
    social:{ facebook:'', instagram:'', twitter:'', discord:'' },
    license_type:'private_home_game',
    house_rules:'',
    parking:'',
    food_drinks:'',
    amenities:'',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const set = (f: string, v: any) => setForm(prev => ({ ...prev, [f]: v }));

  const handleSubmit = async () => {
    if (!user?.uid) { setError('You must be signed in.'); return; }
    if (!form.name || !form.city) { setError('Venue name and city are required.'); return; }
    setSubmitting(true); setError('');
    try {
      // Check for existing venue by this TD
      const existing = await getDocs(query(collection(db,'venues'), where('td_uid','==',user.uid)));
      if (existing.size >= 3) {
        setError('You can have a maximum of 3 venue profiles.'); setSubmitting(false); return;
      }
      const docRef = await addDoc(collection(db,'venues'), {
        slug: generateSlug(form.name),
        trial_started_at: serverTimestamp(),
        trial_expires_at: new Date(Date.now() + 30 * 24 * 3600000).toISOString(),
        td_uid: user.uid,
        td_name: user.displayName ?? 'Tournament Director',
        name: form.name,
        description: form.description,
        schedule: form.schedule,
        game_type: form.game_type,
        buy_in_range: form.buy_in_range,
        area: form.area,
        city: form.city,
        province: form.province,
        brand_color_primary: form.brand_color_primary,
        brand_color_secondary: form.brand_color_secondary,
        social: form.social,
        license_type: form.license_type,
        house_rules: form.house_rules,
        parking: form.parking,
        food_drinks: form.food_drinks,
        amenities: form.amenities,
        pctds_status: 'none',
        followers: [],
        event_count: 0,
        avg_rating: 0,
        rating_count: 0,
        logo_url: null,
        banner_url: null,
        created_at: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      navigate(`/venue/${docRef.id}`);
    } catch(e) { console.error(e); setError('Something went wrong. Please try again.'); }
    setSubmitting(false);
  };

  return (
    <Box maxW="600px" mx="auto" px={4} py={8} fontFamily={F.ui}>
      <Heading style={{fontFamily:F.ui,color:C.white,fontSize:26,marginBottom:6}}>
        Create a Venue Profile
      </Heading>
      <Text style={{fontFamily:F.ui,fontSize:13,color:C.dim,lineHeight:1.6,marginBottom:24}}>
        Create a public venue profile for your regular game. Players can follow your venue and get notified of upcoming events. Apply for PCTDS verification after creating your profile.
      </Text>

      <Box bg={C.card} border={`1px solid ${C.border}`} borderRadius={10} overflow="hidden">
        <Box bg="#0D1520" px={4} py={3} borderBottom={`1px solid ${C.border}`}>
          <Text style={{fontFamily:F.mono,fontSize:11,color:C.teal,letterSpacing:'0.2em'}}>VENUE DETAILS</Text>
        </Box>
        <Box p={5}>
          <Flex direction="column" gap={5}>

            <Box>
              <label style={lbl}>Venue Name *</label>
              <input style={inp} placeholder="e.g. Wayne's Thursday Night Hold'em"
                value={form.name} onChange={e=>set('name',e.target.value)} />
            </Box>

            <Box>
              <label style={lbl}>Description</label>
              <Box as="textarea" rows={3} style={{...inp,resize:'none' as any}}
                placeholder="Tell players about your game — atmosphere, experience level, house rules..."
                value={form.description} onChange={(e:any)=>set('description',e.target.value)} />
            </Box>

            <Box>
              <label style={lbl}>Schedule</label>
              <input style={inp} placeholder="e.g. Every Thursday 7:00 PM"
                value={form.schedule} onChange={e=>set('schedule',e.target.value)} />
            </Box>

            <Flex gap={3}>
              <Box flex={1}>
                <label style={lbl}>Game Type</label>
                <select style={{...inp,cursor:'pointer'}} value={form.game_type} onChange={e=>set('game_type',e.target.value)}>
                  <option value="">Select...</option>
                  {GAME_TYPES.map(g=><option key={g} value={g}>{g}</option>)}
                </select>
              </Box>
              <Box flex={1}>
                <label style={lbl}>Buy-in Range</label>
                <select style={{...inp,cursor:'pointer'}} value={form.buy_in_range} onChange={e=>set('buy_in_range',e.target.value)}>
                  <option value="">Select...</option>
                  {BUY_IN_RANGES.map(b=><option key={b} value={b}>{b}</option>)}
                </select>
              </Box>
            </Flex>

            <Flex gap={3}>
              <Box flex={2}>
                <label style={lbl}>City *</label>
                <input style={inp} placeholder="e.g. Winnipeg"
                  value={form.city} onChange={e=>set('city',e.target.value)} />
              </Box>
              <Box flex={1}>
                <label style={lbl}>Province</label>
                <input style={inp} placeholder="e.g. MB"
                  value={form.province} onChange={e=>set('province',e.target.value)} />
              </Box>
            </Flex>

            <Box>
              <label style={lbl}>Area / Neighbourhood</label>
              <input style={inp} placeholder="e.g. River Heights (optional — no exact address)"
                value={form.area} onChange={e=>set('area',e.target.value)} />
            </Box>

            {/* Brand colors */}
            <Box>
              <Text style={{...lbl,marginBottom:12}}>Brand Colors</Text>
              <Flex gap={6}>
                <Box>
                  <Text style={{fontFamily:F.mono,fontSize:10,color:C.dim,marginBottom:6}}>PRIMARY</Text>
                  <Flex align="center" gap={2}>
                    <input type="color" value={form.brand_color_primary}
                      onChange={e=>set('brand_color_primary',e.target.value)}
                      style={{width:40,height:36,borderRadius:4,border:'none',cursor:'pointer'}} />
                    <Box w="60px" h="36px" borderRadius={6}
                      style={{background:form.brand_color_primary}} />
                  </Flex>
                </Box>
                <Box>
                  <Text style={{fontFamily:F.mono,fontSize:10,color:C.dim,marginBottom:6}}>SECONDARY</Text>
                  <Flex align="center" gap={2}>
                    <input type="color" value={form.brand_color_secondary}
                      onChange={e=>set('brand_color_secondary',e.target.value)}
                      style={{width:40,height:36,borderRadius:4,border:'none',cursor:'pointer'}} />
                    <Box w="60px" h="36px" borderRadius={6}
                      style={{background:form.brand_color_secondary}} />
                  </Flex>
                </Box>
              </Flex>
              <Text style={{fontFamily:F.ui,fontSize:12,color:C.dim,marginTop:8}}>
                These colors appear on your venue profile and branded event posters.
              </Text>
            </Box>

            {/* Social links */}
            <Box>
              <Text style={{...lbl,marginBottom:12}}>Social Links (optional)</Text>
              <Flex direction="column" gap={3}>
                {(['facebook','instagram','discord'] as const).map(platform => (
                  <Flex key={platform} align="center" gap={3}>
                    <Text style={{fontFamily:F.mono,fontSize:11,color:C.dim,
                      width:72,textTransform:'capitalize'}}>{platform}</Text>
                    <input value={form.social[platform]}
                      onChange={e=>set('social',{...form.social,[platform]:e.target.value})}
                      placeholder={`https://${platform}.com/...`}
                      style={{...inp,flex:1}} />
                  </Flex>
                ))}
              </Flex>
            </Box>

            {error && (
              <Box bg="#2D0D0D" border={`1px solid ${C.red}`} borderRadius={6} p={3}>
                <Text style={{fontFamily:F.ui,fontSize:13,color:'#FCA5A5'}}>{error}</Text>
              </Box>
            )}

            {/* PCTDS teaser */}
            <Box bg="#1A0F00" border={`1px solid ${C.gold}`} borderRadius={8} p={3}>
              <Flex align="center" gap={3}>
                <Text style={{fontSize:20}}>🏆</Text>
                <Text style={{fontFamily:F.ui,fontSize:13,color:C.ink,lineHeight:1.6}}>
                  After creating your venue, you can apply for <b style={{color:C.gold}}>PCTDS verification</b> to get the verified badge, poster generator, and sanctioned event tools.
                </Text>
              </Flex>
            </Box>

            <Box as="button" px={6} py={4}
              bg={submitting?C.card:'#052E16'} border={`1px solid ${submitting?C.border:'#166534'}`}
              borderRadius={8} fontSize="15px" fontWeight="700"
              color={submitting?C.dim:'#86EFAC'} fontFamily={F.ui}
              cursor={submitting?'wait':'pointer'} opacity={submitting?0.7:1}
              onClick={handleSubmit} disabled={submitting}>
              {submitting?'Creating...':'Create Venue Profile →'}
            </Box>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateVenuePage;
